// @flow
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { PrescriptionJobProvider } from 'js/components/Prescription/PrescriptionJobContext';
import WebAPIUtils from '../../WebAPIUtils';
import { useFarm } from 'js/context/AccountContext';

import PrescriptionEditorContainer from '../Prescription/PrescriptionEditorContainer';
import PrescriptionJobDownloadContainer from 'js/components/Prescription/PrescriptionJobDownloadContainer';
import FullScreenPortalPaper from 'js/components/Prescription/FullScreenPortalPaper';
import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import {
  isSatelliteLayer,
  isSurveyLayer,
  MetaTypes,
  PrescriptionJob,
  SOURCE_CATEGORY,
} from 'js/components/Prescription/PrescriptionJob';
import ViewModeConstants from 'js/constants/ViewModeConstants';
import { setViewMode } from 'js/reducers/ControlReducer';
import { PRESCRIPTION_METERING, PRESCRIPTION_UNIT } from 'js/constants/PrescriptionConstants';
import { connect } from 'react-redux';
import {
  deletePrescriptionDocumentation,
  deletePrescriptionJobs,
  getPrescriptionDocumentation,
  getPrescriptionMaps,
  resetBulkPrescriptionFutureJob,
  resetBulkPrescriptions,
  savePrescriptionJobLocally,
  savePrescriptionJobPromise,
  setBulkPrescriptionFutureJob,
  setBulkPrescriptionJob,
  setBulkPrescriptionJobs,
  setCurrentJob,
} from 'js/reducers/PrescriptionReducer';
import PrescriptionExitSaveDialog from '../Prescription/Dialogs/PrescriptionExitSaveDialog';
import { useLangFile } from 'js/context/LanguageContext';
import {
  convertWktTo3DArray,
  createFieldsMap,
  resetFilters,
  selectField,
} from 'js/reducers/FieldReducer';
import PrescriptionArchive from './PrescriptionArchive';
import { useSeasonContext } from '../../context/SeasonContext';
import { selectSeason } from '../../reducers/SeasonReducer';
import PrescriptionArchiveConfirmDeleteDialog from './PrescriptionArchiveConfirmDeleteDialog';
import {
  resetCurrentJob,
  resetFutureJob,
  setFutureJob,
  setPrescriptionJobSeasonId,
  updateFutureJob,
} from '../../reducers/PrescriptionReducer';
import { formatSeason } from '../../helpers/SeasonUtils';
import { Action, useActionSnackbarContext } from '../ActionSnackbarHandler/ActionSnackbarHandler';
import Season from '../../model/Season';
import { setSelectedLayer, setSelectedSurveyForcefully } from '../../reducers/SurveyReducer';
import { useSelectedSurvey } from '../../context/SurveyContext';
import { SATELLITE_LAYERS } from '../../constants/SatelliteLayers';
import { YIELD_LAYERS } from '../../constants/YieldLayer';
import { fetchCropHealth } from '../../reducers/FieldReducer';
import { ShowCurrentJobProvider } from '../Prescription/PrescriptionJobContext';
import MultipleActionsDialog from '../Prescription/Dialogs/MultipleActionsDialog.jsx';
import { mergePrescriptionJobsValues } from '../Prescription/LimeJob/limeUtils';
import {
  calculateIntervalAreas,
  calculateLimeJobIntervals,
  calculateOverridesArea,
} from '../Prescription/PrescriptionUtils';
import { SURVEY_LAYERS } from '../../constants/SurveyLayers';
import { PrescriptionEditorProvider } from '../Prescription/PrescriptionEditorContext';
import { DefaultLimePrescriptionStrategy } from '../Prescription/LimeJob/LimeJobStrategies/DefaultLimePrescriptionStrategy';
import { AlternativeLimePrescriptionStrategy } from '../Prescription/LimeJob/LimeJobStrategies/AlternativeLimePrescriptionStrategy';
import PrescriptionUpdateJobDialog from '../Prescription/Dialogs/PrescriptionUpdateJobDialog';
import { formatDateAsYearMonthDate, formatDateAsYearMonthDateTime } from '../../helpers/DateUtils';
import { hideProgress, showProgress } from '../../reducers/ProgressReducer';
import {
  DATA_INTEGRATION_CLAAS_CONNECT,
  DATA_INTEGRATION_EXATREK,
} from '../../reducers/DataIntegrationsReducer';
import areaCalculation from '../../algorithms/geometry/AreaCalculation';
import wellknown from 'wellknown';
import booleanEqual from '@turf/boolean-equal';
import { getFieldName } from '../../helpers/StateInterpreters';
import { getPrescriptionStrategyFromRef } from '../../model/prescription/PrescriptionModelsAndVersions';
import { usePrescriptionJobOrchestration } from '../Prescription/SeedingJob/usePrescriptionJobOrchestration';
import { getBulkJobsForSaving, getFieldForPrescriptionEditor } from './PrescriptionArchiveUtils';

/* endregion IMPORTS */

const getLatestDate = (fieldStatsDatesArray: Array) => {
  const sortedFieldInfo = [...fieldStatsDatesArray].sort(function (a, b) {
    return new Date(b) - new Date(a);
  });
  return sortedFieldInfo[0];
};

const runAsyncJob = (loadJob) =>
  new Promise((resolve, reject) => {
    loadJob(resolve, reject).catch(reject);
  });

const mapStateToProps = (store) => ({
  maps: store.prescription.maps,
  documentation: store.prescription.documentation,
  currentJob: store.prescription.currentJob,
  viewMode: store.control.viewMode,
  images: store.field.images,
  date: store.field.date?.date,
  selectedField: store.field.selectedField,
  selectedLayer: store.survey.selectedLayer,
  showSatelliteImagery: store.field.showSatelliteImagery,
  showSoilSurveys: store.survey.showSoilSurveys,
  surveys: store.survey.surveys,
  futureJob: store.prescription.futureJob,
  selectedSurveyReferenceValues: store.survey.selectedSurveyReferenceValues,
  dataIntegrations: store.integrations.dataIntegrations,
  yieldData: store.yield.yieldData,
  appliedInitialIntervals: store.prescription.bulkPrescription.appliedInitialIntervals,
  bulkPrescriptionJobs: store.prescription.bulkPrescription.currentJobs,
  bulkFuturePrescriptionJobs: store.prescription.bulkPrescription.futureJobs,
  isBulkPrescription: store.prescription.isBulkPrescription,
  bulkPrescriptionPrefix: store.prescription.bulkPrescription.prefix,
  bulkPrescriptionFields: store.prescription.bulkPrescription.fields,
});

const PrescriptionContainer = ({
  dispatch,
  maps,
  documentation,
  currentJob,
  images,
  viewMode,
  date,
  selectedField,
  selectedLayer,
  showSatelliteImagery,
  showSoilSurveys,
  surveys,
  futureJob,
  selectedSurveyReferenceValues,
  dataIntegrations,
  yieldData,
  appliedInitialIntervals,
  bulkPrescriptionJobs,
  bulkFuturePrescriptionJobs,
  isBulkPrescription,
  bulkPrescriptionPrefix,
  bulkPrescriptionFields,
  setPrescriptionJobStrategy,
}) => {
  const LangFile = useLangFile();
  const { selectedSeason, seasons } = useSeasonContext();
  const selectedSurvey = useSelectedSurvey();
  const [showCurrentJob, setShowCurrentJob] = useState(true);

  const hasMultipleJobs = futureJob !== null && futureJob !== undefined;
  const selectedJob = showCurrentJob === true ? currentJob : futureJob;

  const [fields, setFields] = useState(null);

  const [showArchive, setShowArchive] = useState(false);
  const [isResumingJob, setIsResumingJob] = useState(false);
  const editorField = useMemo(
    () => getFieldForPrescriptionEditor(fields, selectedField, selectedJob, isResumingJob),
    [
      fields,
      selectedField,
      selectedJob?.fieldId,
      selectedJob?.fieldSize,
      selectedJob?.bounds,
      selectedJob?.polygon,
      selectedJob?.usingJobPolygon,
      isResumingJob,
    ]
  );
  const [loadingMaps, setLoadingMaps] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showExitSaveDialog, setShowExitSaveDialog] = useState(false);

  const [showUpdateExistingJobLayerSaveDialog, setShowUpdateExistingJobLayerSaveDialog] =
    useState(false);
  const [showUpdateExistingJobLayerDownloadDialog, setShowUpdateExistingJobLayerDownloadDialog] =
    useState(false);

  const [showOverwriteExistingJobSaveDialog, setShowOverwriteExistingJobSaveDialog] =
    useState(false);
  const [showOverwriteExistingJobDownloadDialog, setShowOverwriteExistingJobDownloadDialog] =
    useState(false);

  const [isExiting, setIsExiting] = useState(false);
  const [loadingJob, setLoadingJob] = useState(false);
  const [downloadJob, setDownloadJob] = useState(null);
  const [savingJob, setSavingJob] = useState(false);
  const [savingError, setSavingError] = useState(false);
  const [deleteJob, setDeleteJob] = useState(null);

  const [datesWithImages, setDatesWithImages] = useState([]);

  const { addAction } = useActionSnackbarContext();
  const { recreatePrescriptionMapForExistingJob } = usePrescriptionJobOrchestration();

  const farm = useFarm();

  const setPrescriptionJob = (changes, recalculate, forceCurrentJob) => {
    if (showCurrentJob || forceCurrentJob) {
      dispatch(setCurrentJob({ ...changes }, recalculate));
    } else {
      dispatch(updateFutureJob({ ...changes }));
    }
  };

  useEffect(() => {
    if (
      !selectedField ||
      selectedField.fieldId !== selectedJob?.fieldId ||
      !selectedField.bundle ||
      !fields ||
      (selectedSurvey && !selectedSurvey.fieldId)
    ) {
      return;
    }
    const bundleId = selectedField.bundle;

    const updateSurveySelection = (surveyForJob, date) => {
      if (
        surveyForJob?.surveyImageId &&
        selectedSurvey?.surveyImageId !== surveyForJob?.surveyImageId
      ) {
        setPrescriptionJob({ survey: surveyForJob });
        dispatch(setSelectedSurveyForcefully(surveyForJob));
      }

      return formatDateAsYearMonthDate(surveyForJob?.publishDate || date);
    };

    const getDateForPrescriptionJob = (dates) => {
      const formattedJobDate = formatDateAsYearMonthDate(selectedJob.date);

      if (
        (isSatelliteLayer(selectedLayer) && dates.includes(formattedJobDate)) ||
        isSurveyLayer(selectedLayer)
      ) {
        return formattedJobDate;
      }

      return getLatestDate(dates);
    };

    const handleSurvey = (dateForPrescriptionJob) => {
      let surveyForJob = surveys.find(
        (survey) =>
          survey.fieldId === selectedJob.fieldId &&
          formatDateAsYearMonthDateTime(survey.publishDate) ===
            formatDateAsYearMonthDateTime(selectedJob.date) &&
          Object.keys(survey.newImages).includes(selectedLayer)
      );

      if (!surveyForJob) {
        surveyForJob = surveys.find(
          (survey) =>
            survey.fieldId === selectedJob.fieldId &&
            Object.keys(survey.newImages).includes(selectedLayer)
        );
      }

      return surveyForJob;
    };

    // gets historical data on field, including dates for initial job creation
    if (!appliedInitialIntervals) {
      WebAPIUtils.getBundleStats(bundleId, 'ndvi').then((result) => {
        if (!result || result.length === 0) {
          setDatesWithImages(null);
          return;
        }

        const dates = result.map((stat) => stat.date);
        setDatesWithImages(dates);

        let dateForPrescriptionJob = getDateForPrescriptionJob(dates);

        let surveyAssets = null;
        if (isSurveyLayer(selectedLayer)) {
          const surveyForJob = handleSurvey(dateForPrescriptionJob);
          dateForPrescriptionJob = updateSurveySelection(surveyForJob, dateForPrescriptionJob);
          surveyAssets = selectedSurvey ? handleSurveyAssets() : null;
        }

        const fetchDate = isSurveyLayer(selectedLayer)
          ? getLatestDate(dates)
          : dateForPrescriptionJob;

        // Fetching data to get image on field on the latest date the field has an image
        if (
          selectedJob.polygonWKTString &&
          selectedField.polygonWkt !== selectedJob.polygonWKTString
        )
          return;

        dispatch(fetchCropHealth(farm.farmId, fetchDate, fields)).then((result) => {
          // Handling assets on the new date
          const currentImages = result.value;
          const fieldImages =
            currentImages && currentImages.find((image) => image.field_id === selectedJob.fieldId);
          const assets = { ...setSatelliteAssets(fieldImages), ...surveyAssets };

          setPrescriptionJob({
            assets: {
              ...selectedJob.assets,
              ...assets,
            },
            date: new Date(dateForPrescriptionJob),
          });
        });
      });
    }
  }, [fields, selectedSurvey, selectedField, selectedJob?.fieldId]);

  const handleSurveyAssets = () => {
    if (selectedJob.category !== SOURCE_CATEGORY.VITALITY) {
      return;
    }
    const surveyImages = Object.keys(selectedSurvey.newImages);
    const jobAssets = Object.keys(selectedJob.assets);

    const isMissingImages = surveyImages.some((i) => jobAssets.includes(i) === false);

    if (!isMissingImages) {
      return;
    }

    return setSurveyAssets();
  };

  const setYieldAssets = (fieldId) => {
    let images = [];
    const connectedDataIntegrations = dataIntegrations.filter(
      (integration) => integration.connected
    );
    try {
      let exatrekFieldAssets = null;
      let claasFieldAssets = null;

      if (connectedDataIntegrations.some((integration) => integration.name === 'EXATREK')) {
        exatrekFieldAssets = yieldData[fieldId]?.filter(
          (entry) => entry.source === DATA_INTEGRATION_EXATREK
        );
      }

      if (connectedDataIntegrations.some((integration) => integration.name === 'CLAAS-CONNECT')) {
        claasFieldAssets = yieldData[fieldId]?.filter(
          (entry) => entry.source === DATA_INTEGRATION_CLAAS_CONNECT
        );
      }

      // Exatrek
      if (exatrekFieldAssets && exatrekFieldAssets.length > 0) {
        const exatrekImages = exatrekFieldAssets.map((asset) => ({
          id: asset.id,
          seasonId: asset.seasonId,
          url: asset.url,
          date: asset.date,
          colorIntervals: asset.colorIntervals,
          source: asset.source,
        }));
        images = images.concat(exatrekImages);
      }

      // Claas
      if (claasFieldAssets && claasFieldAssets.length > 0) {
        const claasImages = claasFieldAssets.map((asset) => ({
          id: asset.id,
          seasonId: asset.seasonId,
          url: asset.url,
          date: asset.date,
          colorIntervals: asset.colorIntervals,
          source: asset.source,
        }));
        images = images.concat(claasImages);
      }

      images.sort((a, b) => new Date(a.date) - new Date(b.date));

      // return null if there are no images, to avoid adding empty yield assets
      if (images.length === 0) {
        return null;
      }

      return { [YIELD_LAYERS.YIELD]: { images } };
    } catch (error) {
      console.error('Error fetching yield data', error);
    }
  };

  useEffect(() => {
    if (
      selectedJob &&
      selectedJob.date &&
      selectedJob.metaType === MetaTypes.LIME &&
      selectedJob.vrmSet?.jobIndex !== 1 &&
      isSatelliteLayer(selectedLayer)
    ) {
      const jobDateStr = formatDateAsYearMonthDate(selectedJob.date.toISOString());

      let surveyForJob = surveys.find(
        (survey) =>
          survey.fieldId === selectedJob.fieldId &&
          formatDateAsYearMonthDate(survey.publishDate) === jobDateStr
      );

      if (surveyForJob) {
        return;
      }

      surveyForJob = surveys.find((survey) => survey.fieldId === selectedJob.fieldId);

      const surveyDateStr = formatDateAsYearMonthDate(surveyForJob.publishDate);

      if (jobDateStr !== surveyDateStr) {
        setPrescriptionJob({
          ...selectedJob,
          date: new Date(surveyForJob.publishDate),
        });
      }
    }
  }, [selectedJob?.metaType, selectedJob?.date, selectedLayer]);

  useEffect(() => {
    setShowArchive(viewMode === ViewModeConstants.PRESCRIPTION_ARCHIVE);
  }, [viewMode]);

  useEffect(() => {
    if (selectedSeason) {
      let active = true;
      setFields(null);
      WebAPIUtils.getSeasonFields(selectedSeason.id).then((result) => {
        if (active && result && result.fields) {
          setFields(createFieldsMap(result.fields));
        }
      });
      return () => {
        active = false;
      };
    }
  }, [selectedSeason?.id]);

  useEffect(() => {
    if (selectedJob?.prescriptionModel) {
      setPrescriptionJobStrategy(getPrescriptionStrategyFromRef(selectedJob.prescriptionModel));
    } else {
      setPrescriptionJobStrategy(null);
    }
  }, [selectedJob?.prescriptionModel]);

  // Create new Prescription Job
  useEffect(() => {
    if (viewMode === ViewModeConstants.PRESCRIPTION) {
      if (selectedField) {
        if (!loadingJob && !selectedJob) {
          const job = new PrescriptionJob();
          job.seasonId = selectedSeason.id;
          job.fieldId = selectedField.fieldId;
          job.fieldSize = selectedField.size;
          job.bounds = selectedField.bounds;
          job.date = new Date(date);
          job.unit = PRESCRIPTION_UNIT.KILOGRAM;
          job.metaType = ''; // Default
          job.metering = PRESCRIPTION_METERING.HA; // Default
          job.saved = false;
          job.polygon = selectedField.polygonWkt;

          if (showSatelliteImagery) {
            job.layer = SATELLITE_LAYERS.VITALITY_NDVI;
          } else if (showSoilSurveys) {
            job.layer = selectedLayer;
          } else {
            job.layer = SATELLITE_LAYERS.VITALITY_NDVI;
          }
          if (selectedSurvey && selectedSurvey.newImages) {
            job.surveyImageId = selectedSurvey.surveyImageId;
            job.assets = { ...job.assets, ...setSurveyAssets() };
          }

          job.assets = { ...job.assets, ...setYieldAssets(job.fieldId) };

          if (images) {
            job.assets = { ...job.assets, ...createJobAssets(job.date, job.fieldId) };
          }

          setPrescriptionJob(job);

          // initialize bulk prescription jobs
          if (isBulkPrescription) {
            const jobs = {};

            const getSurveyForField = (fieldId) => {
              return (
                surveys.find(
                  (survey) =>
                    survey.fieldId === fieldId &&
                    formatDateAsYearMonthDateTime(survey.publishDate) === date
                ) || surveys.find((survey) => survey.fieldId === fieldId)
              );
            };

            bulkPrescriptionFields.forEach((field) => {
              const surveyForField = getSurveyForField(field.fieldId);

              const fieldId = field.fieldId;

              jobs[fieldId] = new PrescriptionJob();
              jobs[fieldId].seasonId = selectedSeason.id;
              jobs[fieldId].fieldId = field.fieldId;
              jobs[fieldId].fieldSize = field.size;
              jobs[fieldId].bounds = field.bounds;
              if (showSatelliteImagery) {
                jobs[fieldId].date = new Date(date);
              } else if (showSoilSurveys) {
                jobs[fieldId].date = surveyForField
                  ? new Date(surveyForField.publishDate)
                  : new Date(date);
              }
              jobs[fieldId].unit = PRESCRIPTION_UNIT.KILOGRAM;
              jobs[fieldId].metaType = '';
              jobs[fieldId].metering = PRESCRIPTION_METERING.HA;
              jobs[fieldId].saved = false;
              jobs[fieldId].polygon = field.polygonWkt;
              if (showSatelliteImagery) {
                jobs[fieldId].layer = SATELLITE_LAYERS.VITALITY_NDVI;
              } else if (showSoilSurveys) {
                jobs[fieldId].layer = selectedLayer;
              }

              if (surveyForField) {
                jobs[fieldId].assets = Object.assign(
                  surveyForField.newImages,
                  createJobAssets(jobs[fieldId].date, fieldId)
                );
                jobs[fieldId].surveyImageId = surveyForField.surveyImageId;
              } else {
                jobs[fieldId].assets = createJobAssets(jobs[fieldId].date, fieldId);
              }
              jobs[fieldId].assets = { ...jobs[fieldId].assets, ...setYieldAssets(fieldId) };
            });

            dispatch(setBulkPrescriptionJobs(jobs));
          }
        }
      }
    }
  }, [
    viewMode,
    selectedJob,
    images,
    loadingJob,
    selectedField,
    date,
    selectedSeason,
    selectedSurvey,
  ]);

  // Load Prescription Maps for Archive
  useEffect(() => {
    if (showArchive && fields != null) {
      setLoadingMaps(true);
      Promise.all([
        dispatch(getPrescriptionMaps(farm, fields)),
        dispatch(getPrescriptionDocumentation(farm.farmId)),
      ])
        .then(() => {
          setLoadingMaps(false);
        })
        .catch((e) => {
          setLoadingMaps(false);
        });
    }
  }, [farm, showArchive, fields]);

  const getLimePrescriptionStrategy = () => {
    // FOR NOW HARDCODED TO Default
    const country = 'Default';
    if (country === 'Default') {
      return new DefaultLimePrescriptionStrategy();
    } else if (country === 'Alternative') {
      return new AlternativeLimePrescriptionStrategy();
    } else {
      throw new Error('Unsupported country strategy');
    }
  };

  // DOWNLOADING PRESCRIPTION JOBS
  const fetchJobWithValues = (job: PrescriptionJob, saveFirst: boolean, overwrite: boolean) => {
    return runAsyncJob(async (resolve, reject) => {
      let result: PrescriptionJob = job;
      if (saveFirst) {
        const jobIdContainsLetters = isNaN(currentJob.jobId);
        const isLayerCategoryChanged =
          (isSurveyLayer(currentJob.layer) && !jobIdContainsLetters) ||
          (isSatelliteLayer(currentJob.layer) && jobIdContainsLetters);
        if (isLayerCategoryChanged && selectedJob.jobId) {
          setShowUpdateExistingJobLayerDownloadDialog(true);
          return;
        }
        if (!isLayerCategoryChanged && selectedJob.jobId && !selectedJob.saved && !overwrite) {
          setShowOverwriteExistingJobDownloadDialog(true);
          return;
        }

        try {
          setSavingJob(true);
          setSavingError(false);
          dispatch(showProgress(LangFile.PrescriptionSaveDialog.saving));
          result = await savePrescriptionJobPromise(result, farm).then((res) => {
            dispatch(hideProgress());
            return res;
          });
          dispatch(savePrescriptionJobLocally(result));
          setPrescriptionJob(result);
          setSavingError(false);
          setSavingJob(false);
          addAction(
            new Action(result.jobId, LangFile.PrescriptionContainer.jobSaved, 'success', 'filled')
          );
        } catch (e) {
          setSavingError(true);
          setSavingJob(false);
          setLoadingJob(false);
          dispatch(hideProgress());
          addAction(
            new Action(
              selectedJob.jobId,
              `${LangFile.PrescriptionContainer.regularDownloadFailed}: ${e.response.data.message}`,
              'error',
              'filled'
            )
          );
          reject(new Error(LangFile.PrescriptionContainer.failedToSaveJob));
          return;
        }
      }

      // Save went well or was skipped
      if (isSatelliteLayer(result.layer)) {
        const data = await WebAPIUtils.getVariableRateMap(result.jobId);
        // required to make sure we have the required TIFF available for creating the VRM backend
        // let payload = await dispatch(fetchCropHealth(farm.farmId, moment(data.date).format("YYYY-MM-DD"), fields));
        // if (payload.value && payload.value.length > 0) {
        result = PrescriptionJob.fromNdviData(data, fields);
        // }
        // else {
        //   setSavingJob(false);
        //   setLoadingJob(false);
        //
        //   reject(new Error("The required TIFF was not found for this VRM."));
        //   return;
        // }
      } else {
        const data = await WebAPIUtils.getPrescriptionJob(farm, result);
        result = PrescriptionJob.fromSurveyData(data, fields);

        let resultSurveys;
        // if the job is lime job calculate lime prescription
        if (result.metaType === MetaTypes.LIME) {
          const jobSurvey =
            surveys &&
            surveys.find(
              (survey) =>
                survey.fieldId === result.fieldId && survey.surveyImageId === result.surveyImageId
            );
          const allLayersExistInNewImages =
            jobSurvey &&
            [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY].every((layer) =>
              Object.prototype.hasOwnProperty.call(jobSurvey.newImages, layer)
            );
          if (allLayersExistInNewImages) {
            resultSurveys = [jobSurvey];
          } else {
            resultSurveys =
              surveys && surveys.filter((survey) => survey.fieldId === result.fieldId);
          }

          const values = await WebAPIUtils.getMultipleLayerValues(
            farm.farmId,
            result.fieldId,
            date,
            resultSurveys,
            [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY],
            result.jobId,
            !resultSurveys
          );

          result.humusValues = values[0];
          result.clayValues = values[1];
          result.reactionValues = result.values;

          const limePrescriptionStrategy = getLimePrescriptionStrategy();
          const currentAndFutureValues = limePrescriptionStrategy.calculateLimePrescription(
            result.humusValues,
            result.clayValues,
            result.reactionValues,
            result.maxPrescription / result.fieldSize,
            result.limeInfo.tolerance,
            result.limeInfo.efficiency
          );
          result.values =
            result.vrmSet?.jobIndex === 1
              ? currentAndFutureValues.future
              : currentAndFutureValues.current;
          calculateIntervalAreas(result);
        } else if (result.prescriptionModel) {
          const prescriptionJobStrategy = getPrescriptionStrategyFromRef(result.prescriptionModel);
          setPrescriptionJobStrategy(prescriptionJobStrategy);
          result = await recreatePrescriptionMapForExistingJob(result);
        }
      }

      if (
        (result.polygon && !compareWKT(fields.get(result.fieldId)?.polygonWkt, result.polygon)) ||
        (isSurveyLayer(result.layer) && result.surveyId)
      ) {
        const coordinates = convertWktTo3DArray(result.polygon);

        const toArea = (ring) =>
          areaCalculation({ coords: ring.map(([lng, lat]) => ({ lng, lat })) }).size;

        const fieldArea = toArea(coordinates[0]);
        const fieldHolesArea = coordinates.slice(1).reduce((sum, hole) => sum + toArea(hole), 0);
        const finalArea = fieldArea - fieldHolesArea;

        const outerRing = coordinates[0];
        const lats = outerRing.map(([lng, lat]) => lat);
        const lngs = outerRing.map(([lng, lat]) => lng);
        const bounds = {
          north: Math.max(...lats),
          south: Math.min(...lats),
          east: Math.max(...lngs),
          west: Math.min(...lngs),
        };

        result = {
          ...result,
          polygon: { coordinates },
          polygonWKTString: result.polygon,
          fieldSize: finalArea,
          bounds,
          usingJobPolygon: true,
        };

        if (result.metaType === MetaTypes.LIME) {
          const limePrescriptionStrategy = getLimePrescriptionStrategy();
          const currentAndFutureValues = limePrescriptionStrategy.calculateLimePrescription(
            result.humusValues,
            result.clayValues,
            result.reactionValues,
            result.maxPrescription / result.fieldSize,
            result.limeInfo.tolerance,
            result.limeInfo.efficiency
          );
          result.values =
            result.vrmSet?.jobIndex === 1
              ? currentAndFutureValues.future
              : currentAndFutureValues.current;
        }

        result.overrideAreas = calculateOverridesArea(result);
        calculateIntervalAreas(result);
      }

      setSavingJob(false);
      setLoadingJob(false);
      resolve(result);
    });
  };

  function compareWKT(wktA, wktB) {
    const geoA = wktA ? wellknown.parse(wktA) : false;
    const geoB = wktB ? wellknown.parse(wktB) : false;

    if (!geoA || !geoB) return false;

    return booleanEqual(geoA, geoB);
  }

  const onResumeJob = useCallback(
    (resumeJob: PrescriptionJob) => {
      setShowArchive(false);

      let field = fields.get(resumeJob.fieldId);
      setIsResumingJob(true);
      dispatch(showProgress());
      fetchJobWithValues(resumeJob, false)
        .then((result) => {
          if (!getFieldForPrescriptionEditor(fields, selectedField, result, true)) {
            throw new Error(
              `Field ${result.fieldId} is unavailable for prescription job ${result.jobId}`
            );
          }

          if (result.layer) {
            dispatch(setSelectedLayer(result.layer));
          }
          dispatch(
            setSelectedSurveyForcefully({ surveyImageId: 'RESUMED-JOB', newImages: result.assets })
          );
          const jobToSave: PrescriptionJob = {
            ...result,
          };

          if (result.surveyImageId) {
            const jobSurvey = surveys.find(
              (survey) => survey.surveyImageId === result.surveyImageId
            );
            dispatch(
              setSelectedSurveyForcefully(
                jobSurvey
                  ? { ...jobSurvey, newImages: result.assets }
                  : {
                      surveyImageId: result.surveyImageId,
                      newImages: result.assets,
                      publishDate: result.date,
                    }
              )
            );
            jobToSave.date = jobSurvey
              ? new Date(formatDateAsYearMonthDateTime(jobSurvey.publishDate))
              : result.date;
          }

          setPrescriptionJob({ ...jobToSave, saved: true });
          dispatch(hideProgress());
          dispatch(setViewMode(ViewModeConstants.PRESCRIPTION));
        })
        .catch((e) => {
          console.error('Failed to open prescription job from archive', e);
          dispatch(hideProgress());
          setLoadingJob(false);
          setIsResumingJob(false);
          setShowArchive(true);
        });
    },
    [fields, farm, selectedSurvey]
  );

  const onSetSeason = useCallback(
    (job: PrescriptionJob, seasonId: number) => {
      const season = seasons.find((s: Season) => s.id === seasonId);

      dispatch(setPrescriptionJobSeasonId(farm, job, seasonId))
        .then(() => {
          const message = `${LangFile.PrescriptionContainer.job} ${job.jobName} ${LangFile.PrescriptionContainer.successfullyMoved} ${LangFile.PrescriptionContainer.to} ${formatSeason(season, LangFile)}`;
          addAction(new Action(job.jobId, message, 'success', 'filled'));
        })
        .catch((e) => {
          console.error(e);
          const message = `${LangFile.PrescriptionContainer.failedToMove} ${LangFile.PrescriptionContainer.job} ${job.jobName} ${LangFile.PrescriptionContainer.to} ${formatSeason(season, LangFile)}`;
          addAction(new Action(job.jobId, message, 'success', 'filled'));
        });
    },
    [fields, farm, seasons]
  );

  const onConfirmDeleteJob = useCallback(() => {
    setDeleteJob((current) => {
      const job = maps.find((m) => current === m.jobId);
      dispatch(deletePrescriptionJobs([job], farm));
      return null;
    });
  }, [farm, maps]);

  const multipleDownload = async (jobsToDownload: PrescriptionJob[], saveFirst: boolean) => {
    if (!saveFirst) {
      setDownloadJob(jobsToDownload);
      return;
    }

    setSavingJob(true);
    saveMultipleJobs().then((savedJobs) => {
      setSavingJob(false);
      setDownloadJob(savedJobs);
    });
  };

  const singularDownload = (job: PrescriptionJob, saveFirst: boolean, overwrite: boolean) => {
    if (job.legacy) {
      setDownloadJob([job]);
      return;
    }
    fetchJobWithValues(job, saveFirst, overwrite)
      .then((result) => {
        setDownloadJob([result]);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const downloadBulkJobs = async (saveFirst, downloadMultiple) => {
    dispatch(showProgress(LangFile.PrescriptionSaveDialog.saving));

    try {
      let jobsToDownload;

      if (saveFirst) {
        jobsToDownload = await saveBulkJobs(downloadMultiple);
        addAction(
          new Action('bulk', LangFile.PrescriptionContainer.jobsSaved, 'success', 'filled')
        );
      } else {
        const currentJobsMap = {
          ...bulkPrescriptionJobs,
          [currentJob.fieldId]: {
            ...currentJob,
            jobName: `${bulkPrescriptionPrefix}-${getFieldName(
              fields.get(currentJob.fieldId),
              LangFile
            )}`,
          },
        };

        const futureJobsMap = {
          ...bulkFuturePrescriptionJobs,
          ...(futureJob ? { [futureJob.fieldId]: { ...futureJob } } : {}),
        };

        jobsToDownload = Object.entries(currentJobsMap).flatMap(([fieldId, job]) =>
          futureJobsMap[fieldId] && downloadMultiple ? [job, futureJobsMap[fieldId]] : [job]
        );
      }

      jobsToDownload = jobsToDownload.flat().filter(Boolean);
      setDownloadJob(jobsToDownload);
    } catch (e) {
      addAction(
        new Action(
          'bulk',
          `${LangFile.PrescriptionContainer.bulkDownloadFailed}: ${e.message}`,
          'error',
          'filled'
        )
      );
    } finally {
      dispatch(hideProgress());
    }
  };

  const onDownloadJob = useCallback(
    (shouldDownloadMultipleJobs: boolean, archiveJob: PrescriptionJob) => {
      const jobToUse = archiveJob || currentJob;
      const jobsToDownload: PrescriptionJob[] = [jobToUse, futureJob];

      let downloadAction;
      if (isBulkPrescription) {
        const jobs = Object.values(bulkPrescriptionJobs);
        const shouldSaveFirst = jobs.some(
          (job) =>
            job.saved === false ||
            (shouldDownloadMultipleJobs &&
              bulkFuturePrescriptionJobs[job.fieldId] &&
              bulkFuturePrescriptionJobs[job.fieldId].saved === false)
        );
        downloadAction = () => downloadBulkJobs(shouldSaveFirst, shouldDownloadMultipleJobs);
      } else {
        if (shouldDownloadMultipleJobs) {
          downloadAction = () =>
            multipleDownload(
              jobsToDownload,
              jobsToDownload.some((job) => job.saved === false)
            );
        } else {
          downloadAction = () => singularDownload(jobToUse, jobToUse.saved === false);
        }
      }

      downloadAction();
    },
    [currentJob, futureJob, isBulkPrescription, bulkPrescriptionFields, fields, farm]
  );

  const onResetDownloadJob = useCallback(() => {
    setDownloadJob(null);
  }, []);

  const onDocumentationUploaded = useCallback(() => {
    dispatch(getPrescriptionDocumentation(farm.farmId));
  }, [farm.farmId]);

  const onDeleteDocumentation = useCallback(
    (file) => {
      return dispatch(deletePrescriptionDocumentation(file.id)).then(() => {
        dispatch(getPrescriptionDocumentation(farm.farmId));
      });
    },
    [farm.farmId]
  );

  const onHideSaveDialog = useCallback(() => {
    setShowSaveDialog(false);
    setSavingError(false);
  }, []);

  const onHideExitSaveDialog = useCallback(() => {
    setShowExitSaveDialog(false);
    setSavingError(false);
  }, []);

  const onHideUpdateExistingJobLayerSaveDialog = useCallback(() => {
    setShowUpdateExistingJobLayerSaveDialog(false);
  });

  const onHideUpdateExistingJobLayerDownloadDialog = useCallback(() => {
    setShowUpdateExistingJobLayerDownloadDialog(false);
  });

  const onHideUpdateExistingJobOverwriteSaveDialog = useCallback(() => {
    setShowOverwriteExistingJobSaveDialog(false);
  });

  const onHideUpdateExistingJobOverwriteDownloadDialog = useCallback(() => {
    setShowOverwriteExistingJobDownloadDialog(false);
  });

  const onSaveAsNew = useCallback(
    (downloading: boolean) => {
      selectedJob.jobId = undefined;
      if (isSurveyLayer(selectedJob.layer) && selectedSurvey) {
        selectedJob.survey = selectedSurvey;
      }
      if (downloading) {
        singularDownload(selectedJob, true);
        setShowUpdateExistingJobLayerDownloadDialog(false);
        setShowOverwriteExistingJobDownloadDialog(false);
        return;
      }

      initiateJobSaving().then(() => {
        setShowUpdateExistingJobLayerSaveDialog(false);
        setShowOverwriteExistingJobSaveDialog(false);
      });
    },
    [selectedJob, farm]
  );

  const onSaveOverwrite = useCallback(
    (downloading: boolean) => {
      setSavingJob(true);
      if (downloading) {
        singularDownload(selectedJob, true, true);
        setShowUpdateExistingJobLayerDownloadDialog(false);
        setShowOverwriteExistingJobDownloadDialog(false);
        return;
      }
      saveSingleJob(true).then(() => {
        setSavingJob(false);
        setShowOverwriteExistingJobSaveDialog(false);
      });
    },
    [selectedJob, farm, savingJob]
  );

  const onExit = useCallback(() => {
    dispatch(setViewMode(ViewModeConstants.OVERVIEW));
    dispatch(resetCurrentJob());
    dispatch(resetFutureJob());
    dispatch(resetBulkPrescriptions());
    dispatch(selectField(null));
    dispatch(setSelectedSurveyForcefully());
    setIsResumingJob(false);
  }, []);

  const saveMultipleJobs = async (): Promise<
    [typeof savedCurrentJob, typeof savedFutureJob] | void
  > => {
    try {
      const savedCurrentJob = await savePrescriptionJobPromise(currentJob, farm);
      const savedFutureJob = await savePrescriptionJobPromise(
        { ...futureJob, vrmSet: { ...futureJob.vrmSet, sourceVrmId: savedCurrentJob.jobId } },
        farm
      );
      dispatch(setCurrentJob({ ...savedCurrentJob, saved: true }));
      dispatch(setFutureJob({ ...savedFutureJob, saved: true }));
      addAction(
        new Action(
          savedFutureJob.jobId,
          LangFile.PrescriptionContainer.jobsSaved,
          'success',
          'filled'
        )
      );
      return [savedCurrentJob, savedFutureJob];
    } catch (e) {
      setSavingError(true);
      addAction(
        new Action(
          selectedJob.jobId,
          `${LangFile.PrescriptionContainer.failedToSaveJobs}: ${e.response?.data?.message || e.message}`,
          'error',
          'filled'
        )
      );
    }
  };

  const saveSingleJob = (overwrite: boolean): Promise => {
    if (!selectedJob.jobName) {
      selectedJob.jobName = LangFile.PrescriptionContainer.unnamed;
    }
    const jobIdContainsLetters = isNaN(currentJob.jobId);
    const isLayerCategoryChanged =
      (isSurveyLayer(currentJob.layer) && !jobIdContainsLetters) ||
      (isSatelliteLayer(currentJob.layer) && jobIdContainsLetters);
    if (isLayerCategoryChanged && selectedJob.jobId) {
      setShowUpdateExistingJobLayerSaveDialog(true);
      return Promise.resolve();
    }

    if (!isLayerCategoryChanged && selectedJob.jobId && !selectedJob.saved && !overwrite) {
      setShowOverwriteExistingJobSaveDialog(true);
      return Promise.resolve();
    }
    dispatch(showProgress(LangFile.PrescriptionSaveDialog.saving));
    return savePrescriptionJobPromise(selectedJob, farm)
      .then((job) => {
        dispatch(setCurrentJob({ ...job, saved: true }));
        dispatch(hideProgress());
        addAction(
          new Action(job.jobId, LangFile.PrescriptionContainer.jobSaved, 'success', 'filled')
        );
      })
      .catch((e) => {
        dispatch(hideProgress());
        addAction(
          new Action(
            selectedJob.jobId,
            `${LangFile.PrescriptionContainer.failedToSaveJob}: ${e.response.data.message}`,
            'error',
            'filled'
          )
        );
      });
  };

  const saveBulkJobsInternal = async (currentJobsMap, futureJobsMap, saveMultiple) => {
    const entries = Object.entries(currentJobsMap);

    const results = await Promise.all(
      entries.map(async ([fieldId, job]) => {
        try {
          const savedJob = await savePrescriptionJobPromise(job, farm);
          if (savedJob.fieldId === currentJob.fieldId) {
            dispatch(setCurrentJob({ ...savedJob }));
          }

          let savedFutureJob = null;
          const bulkFutureJob = futureJobsMap[fieldId];
          if (saveMultiple && bulkFutureJob) {
            savedFutureJob = await savePrescriptionJobPromise(
              {
                ...bulkFutureJob,
                vrmSet: { ...bulkFutureJob.vrmSet, sourceVrmId: savedJob.jobId },
              },
              farm
            );
            if (savedFutureJob.fieldId === futureJob?.fieldId) {
              dispatch(setFutureJob({ ...savedFutureJob, saved: true }));
            }
          }

          dispatch(setBulkPrescriptionJob(fieldId, { ...savedJob }));
          if (savedFutureJob) {
            dispatch(setBulkPrescriptionFutureJob(fieldId, { ...savedFutureJob }));
          }

          return [savedJob, savedFutureJob];
        } catch (e) {
          addAction(
            new Action(
              job.jobId,
              `${LangFile.PrescriptionContainer.failedToSaveJobs}: ${e.response?.data?.message || 'Unknown error'}`,
              'error',
              'filled'
            )
          );
          return null;
        }
      })
    );

    return results.filter(Boolean);
  };

  const saveBulkJobs = async (saveMultiple) => {
    dispatch(showProgress(LangFile.PrescriptionSaveDialog.saving));

    try {
      const futureJobsMap = {
        ...bulkFuturePrescriptionJobs,
        ...(futureJob ? { [futureJob.fieldId]: { ...futureJob } } : {}),
      };

      const savedJobs = await saveBulkJobsInternal(
        getBulkJobsForSaving(bulkPrescriptionJobs, currentJob),
        futureJobsMap,
        saveMultiple
      );
      addAction(new Action('bulk', LangFile.PrescriptionContainer.jobsSaved, 'success', 'filled'));

      return savedJobs;
    } catch (e) {
      addAction(
        new Action(
          'bulk',
          `${LangFile.PrescriptionContainer.failedToSaveJobs}: ${e.message}`,
          'error',
          'filled'
        )
      );
    } finally {
      dispatch(hideProgress());
    }
  };

  const initiateJobSaving = (saveMultiple: boolean): Promise => {
    setSavingJob(true);
    let saveJob;
    if (isBulkPrescription) {
      saveJob = () => saveBulkJobs(saveMultiple);
    } else {
      saveJob = saveMultiple ? saveMultipleJobs : saveSingleJob;
    }

    return saveJob().then(() => {
      setSavingJob(false);
      setShowSaveDialog(false);
    });
  };

  const onSaveJob = useCallback(() => {
    if (
      hasMultipleJobs ||
      (isBulkPrescription && Object.keys(bulkFuturePrescriptionJobs).length > 0)
    ) {
      setShowSaveDialog(true);
      return;
    }

    initiateJobSaving(false);
  }, [currentJob, futureJob]);

  const onSaveAndExit = useCallback(() => {
    initiateJobSaving(futureJob !== null).then(() => {
      onExit();
    });
  }, [selectedJob, farm, onExit]);

  const onExitSafe = useCallback(() => {
    setIsExiting(true);

    if (!selectedJob && !downloadJob) {
      dispatch(setViewMode(ViewModeConstants.OVERVIEW));
      return;
    }

    if (downloadJob) {
      onResetDownloadJob();
    }

    if (selectedJob.saved) {
      onExit();
      return;
    }

    setShowExitSaveDialog(true);
  }, [downloadJob, selectedJob]);

  const onSeasonChange = useCallback((seasonId) => {
    dispatch(selectSeason(seasonId));
    dispatch(resetFilters());
  }, []);

  const createJobAssets = (date: Date, fieldId: number) => {
    const currentImages = images.get(formatDateAsYearMonthDate(date));
    const fieldImages = currentImages && currentImages.find((image) => image.field_id === fieldId);
    return setSatelliteAssets(fieldImages);
  };

  const setSatelliteAssets = (fieldImages) => {
    if (fieldImages === null || fieldImages === undefined) {
      return;
    }

    let assets = {};

    if (fieldImages.hasOwnProperty(SATELLITE_LAYERS.VITALITY_NDVI)) {
      assets = {
        ...assets,
        ...{ [SATELLITE_LAYERS.VITALITY_NDVI]: fieldImages[SATELLITE_LAYERS.VITALITY_NDVI] },
      };
    }

    if (fieldImages.hasOwnProperty(SATELLITE_LAYERS.VARIATIONS_NDVI)) {
      assets = {
        ...assets,
        ...{ [SATELLITE_LAYERS.VARIATIONS_NDVI]: fieldImages[SATELLITE_LAYERS.VARIATIONS_NDVI] },
      };
    }

    if (fieldImages.hasOwnProperty(SATELLITE_LAYERS.VISIBLE)) {
      assets = {
        ...assets,
        ...{ [SATELLITE_LAYERS.VISIBLE]: fieldImages[SATELLITE_LAYERS.VISIBLE] },
      };
    }

    return assets;
  };

  const setSurveyAssets = () => {
    const fieldSurveys = surveys.filter((survey) => survey.fieldId === selectedField.fieldId);

    const assets = {};

    fieldSurveys.forEach((survey) => {
      if (survey.newImages) {
        Object.entries(survey.newImages).forEach(([key, value]) => {
          if (!(key in assets)) {
            assets[key] = value;
          }
        });
      }
    });

    return assets;
  };

  const handleOnJobSwitch = (showCurrent: boolean) => {
    setShowCurrentJob(showCurrent);
  };

  const onDeleteFutureJob = useCallback(() => {
    if (futureJob && futureJob.saved) {
      dispatch(deletePrescriptionJobs([futureJob], farm));
    }

    if (isBulkPrescription && !appliedInitialIntervals) {
      Object.entries(bulkFuturePrescriptionJobs).forEach(([fieldId]) => {
        dispatch(resetBulkPrescriptionFutureJob(fieldId));
      });
    } else if (isBulkPrescription && futureJob) {
      dispatch(resetBulkPrescriptionFutureJob(futureJob.fieldId));
    }
    dispatch(resetFutureJob());
  });

  const handleMergePrescriptionJobs = useCallback(() => {
    const updatedFutureJob: PrescriptionJob = { ...futureJob, originalValues: currentJob.values };
    dispatch(setFutureJob(updatedFutureJob));
    const mergedValues = mergePrescriptionJobsValues(currentJob.values, futureJob.values);
    const newIntervals = calculateLimeJobIntervals(mergedValues, true);

    const newJob: PrescriptionJob = {
      ...currentJob,
      values: mergedValues,
      intervals: newIntervals,
    };

    dispatch(setCurrentJob(newJob, true));

    if (isBulkPrescription && !appliedInitialIntervals) {
      const updatedBulkJobs = { ...bulkPrescriptionJobs };

      Object.entries(bulkFuturePrescriptionJobs).forEach(([fieldId, bulkFutureJob]) => {
        const bulkCurrentJob = updatedBulkJobs[fieldId];
        if (!bulkCurrentJob || !bulkFutureJob) return;

        const bulkMergedValues = mergePrescriptionJobsValues(
          bulkCurrentJob.values,
          bulkFutureJob.values
        );
        const bulkNewIntervals = calculateLimeJobIntervals(bulkMergedValues, true);

        updatedBulkJobs[fieldId] = {
          ...bulkCurrentJob,
          values: bulkMergedValues,
          intervals: bulkNewIntervals,
        };

        const jobWithAreas = calculateIntervalAreas(updatedBulkJobs[fieldId]);
        updatedBulkJobs[fieldId] = jobWithAreas;

        dispatch(resetBulkPrescriptionFutureJob(fieldId));
      });

      dispatch(setBulkPrescriptionJobs(updatedBulkJobs));
    }

    onDeleteFutureJob();
  });

  const readyForJob = () => {
    if (isResumingJob) {
      return true;
    }
    if (selectedField?.bundle != null && selectedField?.ready) {
      if (isSurveyLayer(selectedLayer)) {
        return true;
      }
      return datesWithImages !== null && datesWithImages.length > 0;
    }

    return true;
  };

  const renderUpdateExistingJobLayerDialog = (showDialog, downloading: Boolean) => {
    const onCancel = downloading
      ? onHideUpdateExistingJobLayerDownloadDialog
      : onHideUpdateExistingJobLayerSaveDialog;

    return (
      <PrescriptionUpdateJobDialog
        onCancel={onCancel}
        onSaveAsNew={() => onSaveAsNew(showUpdateExistingJobLayerDownloadDialog)}
        savingJob={savingJob}
        open={showDialog}
        info={LangFile.PrescriptionUpdateJobDialog.saveAsNewInfo}
      />
    );
  };

  const renderUpdateExistingJobSameLayerDialog = (showDialog, downloading: Boolean) => {
    const onCancel = downloading
      ? onHideUpdateExistingJobOverwriteDownloadDialog
      : onHideUpdateExistingJobOverwriteSaveDialog;

    return (
      <PrescriptionUpdateJobDialog
        onCancel={onCancel}
        onOverwrite={() => onSaveOverwrite(showOverwriteExistingJobDownloadDialog)}
        onSaveAsNew={() => onSaveAsNew(showOverwriteExistingJobDownloadDialog)}
        savingJob={savingJob}
        open={showDialog}
        info={LangFile.PrescriptionUpdateJobDialog.overwriteJobInfo}
      />
    );
  };

  const initialDate = () => {
    const formattedSelectedJobDate = formatDateAsYearMonthDate(selectedJob.date);
    if (!fields?.get(selectedJob.fieldId)) {
      return formattedSelectedJobDate;
    }

    if (isSatelliteLayer(selectedLayer)) {
      if (datesWithImages.includes(formattedSelectedJobDate)) {
        return formattedSelectedJobDate;
      }
      return datesWithImages[datesWithImages.length - 1];
    }
    if (isSurveyLayer(selectedLayer) || selectedJob.metaType === MetaTypes.LIME) {
      if (
        surveys.find(
          (survey) =>
            survey.fieldId === selectedJob.fieldId &&
            formatDateAsYearMonthDateTime(survey.publishDate) ===
              formatDateAsYearMonthDateTime(selectedJob.date)
        )
      ) {
        return formatDateAsYearMonthDateTime(selectedJob.date);
      }
      return formatDateAsYearMonthDateTime(selectedSurvey?.publishDate);
    }
  };

  const initialSurvey = () => {
    const selectedJobDateSurvey = surveys.find(
      (survey) =>
        survey.fieldId === selectedJob.fieldId &&
        formatDateAsYearMonthDateTime(survey.publishDate) ===
          formatDateAsYearMonthDateTime(selectedJob.date)
    );
    if (selectedJobDateSurvey) {
      return selectedJobDateSurvey;
    }
    return selectedSurvey;
  };

  return (
    <FullScreenPortalPaper>
      {loadingJob && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress variant={'indeterminate'} color={'primary'} size={80} thickness={4} />
        </Box>
      )}

      {viewMode === ViewModeConstants.PRESCRIPTION &&
        !showArchive &&
        selectedJob &&
        readyForJob() && (
          <PrescriptionJobProvider value={[selectedJob, setPrescriptionJob]}>
            <ShowCurrentJobProvider
              value={{
                showCurrentJob: [showCurrentJob, handleOnJobSwitch],
                hasMultipleJobs,
              }}
            >
              <PrescriptionEditorProvider
                imageDates={datesWithImages}
                initialDate={initialDate()}
                initialSurvey={initialSurvey()}
              >
                <PrescriptionEditorContainer
                  selectedField={editorField}
                  updatePrescriptionJob={setPrescriptionJob}
                  onDownloadJob={onDownloadJob}
                  onExitSafe={onExitSafe}
                  onExit={onExit}
                  disableSettingsDialog={datesWithImages === null}
                  onSaveJob={onSaveJob}
                  onDeleteFutureJob={onDeleteFutureJob}
                  isResumingJob={isResumingJob}
                  onMergePrescriptionJobs={handleMergePrescriptionJobs}
                />
              </PrescriptionEditorProvider>

              <MultipleActionsDialog
                open={showSaveDialog}
                title={LangFile.PrescriptionSplitViewContainer.multipleJobs.saveDialog.title}
                message={LangFile.PrescriptionSplitViewContainer.multipleJobs.saveDialog.message}
                loading={savingJob}
                cancelText={LangFile.PrescriptionSplitViewContainer.multipleJobs.saveDialog.cancel}
                onCancel={onHideSaveDialog}
                primaryText={
                  LangFile.PrescriptionSplitViewContainer.multipleJobs.saveDialog.primary
                }
                onPrimary={() => initiateJobSaving(true)}
                secondaryText={
                  LangFile.PrescriptionSplitViewContainer.multipleJobs.saveDialog.secondary
                }
                onSecondary={() => initiateJobSaving(false)}
                error={savingError}
              />

              <PrescriptionExitSaveDialog
                onExit={onExit}
                onCancel={onHideExitSaveDialog}
                onSaveAndExit={onSaveAndExit}
                savingJob={savingJob}
                savingError={savingError}
                open={showExitSaveDialog}
                isBulkPrescription={isBulkPrescription}
                bulkPrescriptionFields={bulkPrescriptionFields}
              />

              {renderUpdateExistingJobLayerDialog(showUpdateExistingJobLayerSaveDialog, false)}
              {renderUpdateExistingJobLayerDialog(showUpdateExistingJobLayerDownloadDialog, true)}
              {renderUpdateExistingJobSameLayerDialog(showOverwriteExistingJobSaveDialog, false)}
              {renderUpdateExistingJobSameLayerDialog(showOverwriteExistingJobDownloadDialog, true)}
            </ShowCurrentJobProvider>
          </PrescriptionJobProvider>
        )}

      {showArchive && fields && (
        <PrescriptionArchive
          maps={maps}
          documentation={documentation}
          loadingMaps={loadingMaps}
          fields={fields}
          onDocumentationUploaded={onDocumentationUploaded}
          onDeleteDocumentation={onDeleteDocumentation}
          onDownload={onDownloadJob}
          onClose={onExitSafe}
          onSetSeason={onSetSeason}
          onEdit={onResumeJob}
          onDelete={setDeleteJob}
          onSeasonChange={onSeasonChange}
        />
      )}

      {downloadJob && (
        <PrescriptionJobDownloadContainer
          legacy={downloadJob.legacy}
          viewMode={viewMode}
          savingJob={savingJob}
          savingError={savingError}
          downloadJob={downloadJob}
          isBulk={isBulkPrescription}
          bulkJobName={bulkPrescriptionPrefix}
          onBack={onResetDownloadJob}
        />
      )}

      <PrescriptionArchiveConfirmDeleteDialog
        open={deleteJob != null}
        onClose={setDeleteJob}
        onConfirm={onConfirmDeleteJob}
      />
    </FullScreenPortalPaper>
  );
};

export default memo(connect(mapStateToProps)(PrescriptionContainer));
